/**
 * Who is signed in, and what the server says they may do.
 *
 * One module owns this because everything else in the console asks the same
 * two questions: is there a session, and is this capability available to it.
 * The answers come from `GET /auth/me` and are held in a context so a screen
 * never has to re-ask.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { apiBase, fetchWithDeadline } from "../lib/api.js";
import { getCsrfToken, refreshCsrfToken, setCsrfToken, _readCsrfCookie } from "../lib/csrf.js";
import { api, hasSession, path, SIGN_IN_HASH } from "./api.js";
import { html } from "./ui.js";
import { activateTimezone } from "./time.js";

const ME_TIMEOUT_MS = 12000;
const ME_PATH = path("get__auth_me");
const LOGOUT_PATH = path("post__auth_logout");

/**
 * Session discovery is a probe, not a logout command.
 *
 * Deliberately not routed through `api()`: that treats any 401 as an expired
 * session and tears the cookie down. At boot a 401 only means "not signed in
 * yet", and destroying the cookie on the way past would sign out an operator
 * whose session was merely slow to validate.
 */
async function probe() {
  /* Always hit /auth/me. Gating on hasSession() (CSRF cookie) races login:
     the session cookie can land before CSRF is readable, and a null probe
     leaves the operator on Sign in after a successful auth. Soft≠Live. */
  const r = await fetchWithDeadline(apiBase() + ME_PATH, { method: "GET" }, ME_TIMEOUT_MS);
  if (r.status === 401 || r.status === 403) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

/**
 * The first write of a session must not pay for a 403. `api()` recovers from a
 * missing CSRF token by refreshing and replaying, but that costs a round trip
 * an operator can feel; taking the token at sign-in costs nothing.
 */
async function warmCsrf() {
  const ck = _readCsrfCookie();
  if (ck) setCsrfToken(ck);
  if (!getCsrfToken()) {
    try { await refreshCsrfToken(); } catch (_) { /* first write will retry */ }
  }
}

/** `daniel.lim` → `DL`. The server has no display name; this is derived, not invented. */
export function initialsOf(username) {
  const parts = String(username || "").split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (!parts.length) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * `/auth/me` answers in server vocabulary. This is the shape the screens read.
 *
 * Fields the server does not carry are absent rather than filled in: there is
 * no display-name column and no cookie-session wall-clock, so the console shows
 * the username without inventing when the browser session ends. SA capability
 * grant expiry (``sa_capabilities_granted_until``) is exposed as ``saGrantedUntil``.
 */
function shape(me) {
  if (!me) return null;
  const username = String(me.username || "");
  return {
    username,
    initials: initialsOf(username),
    email: me.email || "",
    emailVerified: !!me.email_verified_at,
    timezone: String(me.timezone || ""),
    role: me.role || "",
    zones: Array.isArray(me.zones) ? me.zones : [],
    tenant: me.manager_admin || me.parent_admin_username || username,
    caps: Array.isArray(me.sa_capabilities) ? me.sa_capabilities : [],
    /** Unix seconds; null/0 = no SA grant expiry on this principal. */
    saGrantedUntil: (() => {
      const n = Number(me.sa_capabilities_granted_until);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    policy: me.policy && typeof me.policy === "object" ? me.policy : {},
    hasAvatar: !!me.has_avatar,
    avatarRev: Number(me.avatar_rev || 0),
    features: {
      map: !!me.home_map_ui_enabled,
      markerResolve: !!me.home_map_marker_resolve_allowed,
      billing: !!me.billing_enabled,
      cameras: !!me.camera_snapshot_enabled,
      voice: !!me.voice_enabled,
      tenantAcl: !!me.tenant_acl_enabled,
      opsDashboard: !!me.ops_dashboard_eligible,
    },
    catalog: Array.isArray(me.capability_catalog) ? me.capability_catalog : [],
    raw: me,
  };
}

const Ctx = createContext({ me: null, ready: false, error: "", reload() {}, signOut() {} });

/** Everything below this in the tree can ask who is signed in. */
export function SessionProvider({ children }) {
  const [s, setS] = useState({ me: null, ready: false, error: "", offline: false });
  const retry = useRef(0);

  const load = useCallback(async () => {
    try {
      const me = shape(await probe());
      if (me) activateTimezone(me);
      if (me) await warmCsrf(); else setCsrfToken("");
      retry.current = 0;
      setS({ me, ready: true, error: "", offline: false });
      return me;
    } catch (e) {
      // A probe that fails on the network is not a signed-out operator, and
      // treating it as one used to drop them on sign-in mid-shift. Hold the
      // session, say we are reconnecting, and keep trying.
      const error = String((e && e.message) || e);
      const offline = hasSession();
      setS((prev) => ({ me: prev.me, ready: true, error, offline }));
      if (offline) {
        const wait = Math.min(30000, 1000 * 2 ** retry.current++);
        setTimeout(() => { load(); }, wait);
      }
      return null;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * The sign-out screen shows its own confirmation, so it opts out of the
   * redirect. Everywhere else — a menu item, an expired session — landing on
   * sign-in is what the operator expects.
   */
  const signOut = useCallback(async (opts) => {
    try {
      await api(LOGOUT_PATH, {
        method: "POST",
        timeoutMs: 8000,
        skipExpire: true,
      });
    } catch (_) { /* cookie / CSRF may already be gone — local clear still runs */ }
    setCsrfToken("");
    setS({ me: null, ready: true, error: "" });
    if (!opts || opts.redirect !== false) location.hash = SIGN_IN_HASH;
  }, []);

  const value = { me: s.me, ready: s.ready, error: s.error, reload: load, signOut };
  return html`<${Ctx.Provider} value=${value}>${children}<//>`;
}

export function useSession() {
  return useContext(Ctx);
}

/**
 * Whether a capability is available to this operator.
 *
 * `capability_catalog` is the server's own answer, so a screen asks instead of
 * guessing from a role. An id the server never mentioned is unavailable — the
 * default is no, not yes.
 */
export function useCapability(id) {
  const { me } = useSession();
  if (!me) return false;
  const row = me.catalog.find((c) => c && c.id === id);
  return row ? row.enabled !== false : false;
}
